import { useHistory } from 'application/contexts/HistoryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { AdobeTracking } from './Adobe';
import { setupAppInsights } from './AppInsights/setupAppInsights';
import { setupAppInsightsClient } from './AppInsights/setupAppInsightsClient';
import { Chatbot } from './Chatbot/Chatbot';
import { CludoInit, CludoLang, getCludoEngineId } from './Cludo';
import { CookieConsent, CookieConsentTypes } from './CookieConsent';

export const Vendors: React.FC<Content.PageContent['page']> = ({ documentType, properties }) => {
	const { chatbotTenant, defaultOpenChatbot } = properties ?? {};

	const siteContext = useSiteContext();
	const pageContext = usePageContext();
	const { locale: language, asPath: urlPath } = useRouter();
	const { history } = useHistory();

	const adobeDefaultActive = false; // Used for testing. It should always be false

	const [enabledSearchPage, setEnabledSearchPage] = useState(null);
	const [adobeTrackingActive, setAdobeTrackingActive] = useState(adobeDefaultActive);
	const [statisticalCookiesAllowed, setStatisticalCookiesAllowed] = useState(false);

	const { host, chatbotConfiguration } = pageContext ?? {};
	const {
		APP_ENV: environment,
		DOMAIN_PROTOCOL: protocol,
		COOKIE_CONSENT: cookieConsentActive,
		APPINSIGHTS_CONNECTION_STRING: appInsightsConnectionString,
	} = getConfig()?.publicRuntimeConfig ?? {};

	const hostUrl = `${protocol ? `${protocol}://` : ''}${host}`;

	const { settings: siteSettings, theme } = siteContext ?? {};
	const { searchConfiguration, applicationInsights, adobeTracking, segmentAreas } = siteSettings ?? {};
	const { searchPage, searchEnabled, searchOverwriteEngine } = searchConfiguration ?? {};

	const chatbotDetails = {
		active: siteSettings.chatbot?.active,
		chatIntegrityNew: siteSettings.chatbot?.chatIntegrityKeys?.[0].properties.chatIntegrityNew,
		chatIntegrityOld: siteSettings.chatbot?.chatIntegrityKeys?.[0].properties.chatIntegrityOld,
	};

	const searchPageDetails = {
		name: searchPage?.[0].title,
		target: null,
		linkType: searchPage?.[0].linkType,
		url: searchPage?.[0].route !== null ? hostUrl + searchPage?.[0].route?.path : searchPage?.[0].url,
	};

	if (searchPageDetails && searchEnabled && !enabledSearchPage) setEnabledSearchPage(searchPageDetails);
	const lang = language?.split('-')[0] as CludoLang;

	const loadCludoConfig = () => {
		CludoInit({
			searchPage: enabledSearchPage,
			language: lang,
			hideSearchFilters: !segmentAreas?.segmentsEnabled,
			engineId: searchOverwriteEngine ? searchOverwriteEngine : getCludoEngineId(theme, lang),
		});
	};

	const handleConsent = (consent: CookieConsentTypes) => {
		setStatisticalCookiesAllowed(consent.statistic);
		consent.statistic && adobeTracking?.active ? setAdobeTrackingActive(true) : setAdobeTrackingActive(false);
	};
	const { chatbotOnly, settingsId, tenantId } = chatbotTenant?.[0]?.properties ?? { chatbotOnly: false };
	const { chatbotId } = settingsId?.[0]?.properties ?? {};

	useEffect(() => {
		if (!applicationInsights || !applicationInsights.active) return;
		if (!appInsightsConnectionString) return console.error('AppInsights: Connection string missing');
		if (statisticalCookiesAllowed) setupAppInsights(appInsightsConnectionString);
	}, [appInsightsConnectionString, statisticalCookiesAllowed, applicationInsights]);

	useEffect(() => {
		if (!applicationInsights || !applicationInsights.pageTrackingActive) return;
		if (!appInsightsConnectionString) return console.error('AppInsights: Connection string missing');
		if (statisticalCookiesAllowed) setupAppInsightsClient(appInsightsConnectionString);
	}, [appInsightsConnectionString, statisticalCookiesAllowed, applicationInsights]);

	return (
		<>
			{searchPage && searchEnabled && <Script src="/js/cludo.js" onLoad={() => loadCludoConfig()} />}
			{adobeTrackingActive && (
				<AdobeTracking
					pageContext={pageContext}
					siteContext={siteContext}
					language={language}
					documentType={documentType}
					theme={theme}
					environment={environment}
					currentUrl={hostUrl + urlPath}
					previousUrl={history?.previous ? hostUrl + history.previous : 'null'}
					defaultActive={adobeDefaultActive}
				/>
			)}
			{chatbotDetails?.active && (
				<Chatbot
					tenantId={chatbotOnly ? '11' : tenantId}
					settingsId={chatbotId}
					chatbotConfiguration={chatbotConfiguration}
					chatIntegrityNew={chatbotDetails.chatIntegrityNew}
					chatIntegrityOld={chatbotDetails.chatIntegrityOld}
					defaultOpenChatbot={defaultOpenChatbot}
				/>
			)}

			{cookieConsentActive && <CookieConsent consentCallBack={(consent) => handleConsent(consent)}></CookieConsent>}
		</>
	);
};
